import { useState } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { useContactCaptureRecordController } from '../../controllers';
import { getErrorMessage } from '../../helpers';
import { normalizeContactCaptureDomainInput } from '../../helpers/contactCaptureOrigins';
import {
  ContactCapture,
  ContactCaptureCampaign,
  ContactCaptureSettings,
  ContactCaptureType,
} from '../../types';

type Props = {
  controller: ReturnType<typeof useContactCaptureRecordController>;
  campaign: ContactCaptureCampaign | null;
  id?: string;
  name: string;
  domain: string;
  settings: ContactCaptureSettings;
  type: ContactCaptureType;
  validate: () => boolean;
};

type Task = 'saving' | 'publishing' | 'deleting';

export const useContactCapturePersistence = ({
  controller,
  campaign,
  id,
  name,
  domain,
  settings,
  type,
  validate,
}: Props) => {
  const navigate = useNavigate();
  const [task, setTask] = useState<Task | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const persist = async (): Promise<ContactCapture> => {
    if (!controller.companyId) throw new Error('Company is unavailable.');
    // validate() has already required an active join campaign.
    if (!campaign) throw new Error('Choose an active join campaign.');
    const origin = normalizeContactCaptureDomainInput(domain);
    if (!origin) throw new Error('Enter a valid website domain.');
    const persistedType = controller.record && controller.record.status !== 'draft'
      ? controller.record.type
      : type;
    const data = {
      companyId: controller.companyId,
      name: name.trim(),
      // Preserve API-managed lists when this single-domain field has not been edited.
      allowedOrigins: domain === (controller.record?.allowedOrigins?.[0] || '')
        ? controller.record?.allowedOrigins || [origin]
        : [origin],
      type: persistedType,
      campaignId: campaign.id,
      // The API serializer normalizes once at the transport boundary.
      settings,
    };
    return id
      ? controller.update(controller.companyId, id, data)
      : controller.create(data);
  };

  /** A new record moves to its own route, whether the surrounding action succeeded or not. */
  const openSavedRecord = (record: ContactCapture | null) => {
    if (!id && record) navigate(`/contact-capture/${record.id}/edit`, { replace: true });
  };

  const runPersistTask = async (
    runningTask: Extract<Task, 'saving' | 'publishing'>,
    afterPersist: (record: ContactCapture) => Promise<void> | void,
    errorFallback: string,
  ) => {
    if (!validate()) return;
    setTask(runningTask);
    let record: ContactCapture | null = null;
    try {
      record = await persist();
      await afterPersist(record);
      openSavedRecord(record);
    } catch (error) {
      toast.error(getErrorMessage(error, errorFallback));
      openSavedRecord(record);
    } finally {
      setTask(null);
    }
  };

  const saveDraft = () => runPersistTask('saving', record => {
    if (record.hasUnpublishedChanges || record.status === 'draft') {
      toast('Draft saved. Publish the widget to apply the latest changes.', {
        icon: '⚠️',
        duration: 6000,
      });
    } else {
      toast.success('Contact capture updated.');
    }
  }, 'Could not save the contact capture.');

  const publish = () => runPersistTask('publishing', async record => {
    await controller.publish(record.companyId, record.id);
    toast.success('Contact capture published. The latest widget changes are now live.');
  }, 'Could not publish the contact capture.');

  const deleteContactCapture = async () => {
    if (!controller.companyId || !id) return;
    setTask('deleting');
    try {
      await controller.remove(controller.companyId, id);
      toast.success(`${name || 'Contact capture'} deleted.`);
      navigate('/contact-capture', { replace: true });
    } catch (error) {
      toast.error(getErrorMessage(error, 'Could not delete the contact capture.'));
    } finally {
      setTask(null);
      setDeleteDialogOpen(false);
    }
  };

  return {
    busy: task !== null,
    deleteContactCapture,
    deleteDialogOpen,
    deleting: task === 'deleting',
    publish,
    publishing: task === 'publishing',
    saveDraft,
    saving: task === 'saving',
    setDeleteDialogOpen,
  };
};
